import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { IntroAudio } from '../lib/intro-audio.js';
import { audioManager } from '../lib/audio-manager';

type Phase = 'loading-audio' | 'presenting-logo' | 'loading-app' | 'done';

const INTRO_MODE: 'always' | 'once' = 'always';
const SAFETY_TIMEOUT_MS = 7000;
const LOADER_DURATION_MS = 3000;
// After the explosion fully reveals the logo, hold the revealed scene on screen
// for at least this long before transitioning to the loader.
const CUBE_POST_REVEAL_HOLD_MS = 1500;
// Hit sound lands inside the universfield-logo-reveal audio at this offset from
// the moment playback starts (frame-accurate, derived from a 20ms RMS scan).
const AUDIO_HIT_OFFSET_MS = 660;
// Cube animation reaches the smash (flash + tiles cleared + full image revealed)
// at this offset from the moment the cube starts moving.
const CUBE_SMASH_OFFSET_MS =
  800 /* pre-roll */ + 5 * 550 /* steps */ + 250 + 200 + 100 + 400 + 140 /* double jump */;
// Delay audio.play() so the hit aligns exactly with the cube's smash moment:
//   audio_play_time + AUDIO_HIT_OFFSET_MS == cube_smash_time
//   ⟹ audio_play_time = CUBE_SMASH_OFFSET_MS − AUDIO_HIT_OFFSET_MS (3980ms).
const AUDIO_PLAY_DELAY_MS = CUBE_SMASH_OFFSET_MS - AUDIO_HIT_OFFSET_MS;
// Footstep audio lands at the visual landing of each cube step (380ms into
// the 0.4s roll transition). Each cube step fires its own fresh `Audio`
// instance so footsteps don't queue on a single element.
// Footstep audio fires when the cube is *arriving* at the new cell, not after
// it has already settled. The transition is `0.4s cubic-bezier(0.25, 1, 0.5, 1)`,
// which front-loads the motion — the cube reaches ~97% of the new position by
// ~280ms into the 400ms duration and then idles until 400ms. Firing at 380ms
// landed ~100ms AFTER the visual landing; at 280ms it lands right as the
// cube visually touches down.
const FOOTSTEP_LANDING_OFFSET_MS = 280;

const LETTER_PANEL_DATA: Record<string, string[]> = {
  C: ['C', 'C', 'C', 'C', ''],
  A: ['A', 'A', 'A', 'A', ''],
  R: ['R', 'R', 'R', 'R', ''],
  G: ['G', 'G', 'G', 'G', ''],
  N: ['N', 'N', 'N', 'N', ''],
  D: ['D', 'D', 'D', 'D', ''],
  O: ['O', 'O', 'O', 'O', ''],
  E: ['E', 'E', 'E', 'E', ''],
  S: ['S', 'S', 'S', 'S', ''],
  P: ['P', 'P', 'P', 'P', ''],
  I: ['I', 'I', 'I', 'I', ''],
};

@customElement('juegazo-intro')
export class JuegazoIntro extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state() private phase: Phase = 'loading-audio';
  @state() private audioNeedsGesture = true;
  @state() private audioMuted = false;
  @state() private jump1WhooshMuted = true;
  private smashAudio: IntroAudio | null = null;
  private smashPlayScheduled: number | null = null;
  private smashPlayScheduledFired = false;
  private interactionHandler: (() => void) | null = null;
  private readonly smashAudioSrc = '/sounds/universfield-logo-reveal.mp3';
  private readonly footstepAudioSrc = '/sounds/floraphonic-footstep.mp3';
  private readonly whooshAudioSrc = '/sounds/jofae-whoosh.mp3';
  private readonly interStepAudioSrc = '/sounds/u-viypcpsnud-step.mp3';
  private loadingSafetyTimer: number | null = null;
  private presentingSafetyTimer: number | null = null;
  private loaderTimer: number | null = null;
  private animationTimeouts = new Set<number>();
  private animationCancelled = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.applyInert(true);
    this.shouldShow().then((show) => {
      if (show) {
        this.start();
      } else {
        this.skipImmediately();
      }
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cleanup();
    this.applyInert(false);
  }

  public advanceToDone(): void {
    this.finish();
  }

  private async shouldShow(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    if (params.get('intro') === 'off') return false;
    if (INTRO_MODE === 'once' && localStorage.getItem('juegazo.introSeen') === '1') return false;
    return true;
  }

  private skipImmediately(): void {
    this.phase = 'done';
    this.applyInert(false);
  }

  private start(): void {
    this.smashAudio = new IntroAudio({ src: this.smashAudioSrc });
    this.smashAudio.onCanPlay(() => this.advanceToPresenting());
    this.smashAudio.onEnded(() => this.advanceToLoadingApp());
    this.smashAudio.preload();

    // Listen for any document-wide user gesture. Once detected, scheduled
    // `playSmashAudio()` calls (at AUDIO_PLAY_DELAY_MS) will succeed because
    // the browser's autoplay gate has been lifted for this document context.
    this.attachGestureListeners();

    this.loadingSafetyTimer = window.setTimeout(
      () => this.advanceToPresenting(),
      SAFETY_TIMEOUT_MS,
    );
  }

  private attachGestureListeners(): void {
    if (this.interactionHandler) return;
    // The browser tracks user activation globally. Anything user-gesture-fired
    // (pointerdown/keydown/touchstart) lifts the autoplay gate for the whole
    // document, so the scheduled `playSmashAudio` at AUDIO_PLAY_DELAY_MS will
    // resolve later. We do NOT mutate Lit state from here — that races with
    // the button's @click handler (which fires right after the same pointerdown).
    const handler = () => {
      // Touch the wall only if we ever need to re-run mute sync; currently
      // this is a no-op marker so the listener registration is meaningful.
    };
    this.interactionHandler = handler;
    document.addEventListener('pointerdown', handler);
    document.addEventListener('keydown', handler);
    document.addEventListener('touchstart', handler);
  }

  private detachGestureListeners(): void {
    if (!this.interactionHandler) return;
    document.removeEventListener('pointerdown', this.interactionHandler);
    document.removeEventListener('keydown', this.interactionHandler);
    document.removeEventListener('touchstart', this.interactionHandler);
    this.interactionHandler = null;
  }

  private advanceToPresenting(): void {
    if (this.phase !== 'loading-audio') return;
    this.phase = 'presenting-logo';
    if (this.loadingSafetyTimer !== null) {
      clearTimeout(this.loadingSafetyTimer);
      this.loadingSafetyTimer = null;
    }
    this.presentingSafetyTimer = window.setTimeout(
      () => this.advanceToLoadingApp(),
      SAFETY_TIMEOUT_MS,
    );
  }

  private advanceToLoadingApp(): void {
    if (this.phase === 'loading-app' || this.phase === 'done') return;
    this.cancelCubeAnimation();
    this.cancelScheduledSmashAudio();
    this.phase = 'loading-app';
    if (this.presentingSafetyTimer !== null) {
      clearTimeout(this.presentingSafetyTimer);
      this.presentingSafetyTimer = null;
    }
    this.loaderTimer = window.setTimeout(() => this.finish(), LOADER_DURATION_MS);
  }

  private finish(): void {
    if (this.phase === 'done') return;
    this.phase = 'done';
    if (INTRO_MODE === 'once') {
      localStorage.setItem('juegazo.introSeen', '1');
    }
    this.dispatchEvent(
      new CustomEvent('jgz-intro-finished', { bubbles: true, composed: true }),
    );
    requestAnimationFrame(() => {
      const shell = this.shadowRoot?.querySelector('.intro-shell') as HTMLElement | null;
      if (shell) shell.classList.add('intro-fade-out');
      window.setTimeout(() => {
        this.applyInert(false);
        this.remove();
      }, 400);
    });
  }

  private cleanup(): void {
    if (this.loadingSafetyTimer !== null) {
      clearTimeout(this.loadingSafetyTimer);
      this.loadingSafetyTimer = null;
    }
    if (this.presentingSafetyTimer !== null) {
      clearTimeout(this.presentingSafetyTimer);
      this.presentingSafetyTimer = null;
    }
    if (this.loaderTimer !== null) {
      clearTimeout(this.loaderTimer);
      this.loaderTimer = null;
    }
    this.detachGestureListeners();
    this.cancelCubeAnimation();
    this.cancelScheduledSmashAudio();
    this.smashAudio?.setMuted(false);
    this.smashAudio?.destroy();
    this.smashAudio = null;
  }

  private cancelCubeAnimation(): void {
    this.animationCancelled = true;
    for (const id of this.animationTimeouts) {
      clearTimeout(id);
    }
    this.animationTimeouts.clear();
  }

  private applyInert(inert: boolean): void {
    const app = document.querySelector('juegazo-app');
    if (!app) return;
    if (inert) {
      app.setAttribute('inert', '');
    } else {
      app.removeAttribute('inert');
    }
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('phase') && this.phase === 'presenting-logo') {
      this.startCubeAnimation();
    }
    if (changed.has('audioMuted') && this.smashAudio) {
      this.smashAudio.setMuted(this.audioMuted);
    }
  }

  /**
   * 3D cube reveal: a purple cube rolls across a 4x6 grid clearing each tile,
   * exposing the logo behind it. The cube then performs a double jump and explodes
   * with a flash and fireworks before advancing to the loader phase.
   *
   * Footstep audio fires at each step's visual landing (FOOTSTEP_LANDING_OFFSET_MS
   * into the 0.4s roll transition). The smash audio (universfield-logo-reveal) is
   * scheduled at AUDIO_PLAY_DELAY_MS so its hit lands on the cube's smash moment.
   */
  private startCubeAnimation(): void {
    const board = this.querySelector<HTMLElement>('.intro-cube-board');
    const cubePositioner = this.querySelector<HTMLElement>('.intro-cube-positioner');
    const flash = this.querySelector<HTMLElement>('.intro-cube-flash');
    if (!board || !cubePositioner || !flash) return;

    const tiles = Array.from(this.querySelectorAll<HTMLElement>('.intro-cube-tile'));
    const cellSize = 80;
    this.animationCancelled = false;

    const trackTimeout = (cb: () => void, ms: number): void => {
      const id = window.setTimeout(() => {
        this.animationTimeouts.delete(id);
        if (this.animationCancelled) return;
        cb();
      }, ms);
      this.animationTimeouts.add(id);
    };

    const clearTile = (row: number, col: number): void => {
      const idx = row * 4 + col;
      tiles[idx]?.classList.add('cleared');
    };

    let rotator: HTMLElement | null = this.querySelector<HTMLElement>('.intro-cube-rotator');

    const rollGlobal = (axis: 'X' | 'Y', angle: number): void => {
      if (!rotator) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'intro-cube-rotator';
      rotator.parentNode?.insertBefore(wrapper, rotator);
      wrapper.appendChild(rotator);
      void wrapper.offsetWidth;
      wrapper.style.transform = `rotate${axis}(${angle}deg)`;
      rotator = wrapper;
    };

    type Step = { c: number; r: number; axis: 'X' | 'Y' | null; angle: number };
    const path: Step[] = [
      { c: 0, r: 0, axis: null, angle: 0 },
      { c: 1, r: 0, axis: 'Y', angle: 90 },
      { c: 1, r: 1, axis: 'X', angle: -90 },
      { c: 2, r: 1, axis: 'Y', angle: 90 },
      { c: 2, r: 2, axis: 'X', angle: -90 },
    ];

    let currentStep = 0;

    const moveCube = (): void => {
      if (this.animationCancelled || !cubePositioner) return;
      if (currentStep < path.length) {
        const posIdx = currentStep;
        const pos = path[currentStep];
        cubePositioner.style.transform = `translate3d(${pos.c * cellSize}px, ${pos.r * cellSize}px, 40px)`;
        if (pos.axis) rollGlobal(pos.axis, pos.angle);
        clearTile(pos.r, pos.c);
        currentStep++;
        // Only the final path step (path[4], the roll-X giro final right before
        // the double jump) gets the whoosh — a last-second wind-up into the
        // leap. Every other step lands with the regular footstep thud.
        const useWhoosh = posIdx === path.length - 1;
        trackTimeout(
          () => (useWhoosh ? this.playJumpWhoosh() : this.playFootstep()),
          FOOTSTEP_LANDING_OFFSET_MS,
        );
        trackTimeout(moveCube, 550);
      } else {
        const last = path[path.length - 1];
        executeDoubleJump(last.c, last.r);
      }
    };

    const createExplosion = (x: number, y: number): void => {
      if (this.animationCancelled || !board) return;
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = `${x}px`;
      container.style.top = `${y}px`;
      container.style.zIndex = '150';
      board.appendChild(container);

      const numParticles = 60;
      const colors = ['#ffea00', '#ff8c00', '#ffffff', '#00e5ff', '#ff007f'];
      for (let i = 0; i < numParticles; i++) {
        const particle = document.createElement('div');
        particle.className = 'intro-cube-particle';
        const angle = Math.random() * Math.PI * 2;
        const radius = 60 + Math.random() * 220;
        const tx = Math.cos(angle) * radius;
        const ty = Math.sin(angle) * radius;
        particle.style.setProperty('--x', `${tx}px`);
        particle.style.setProperty('--y', `${ty}px`);
        const size = 3 + Math.random() * 6;
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        const color = colors[Math.floor(Math.random() * colors.length)];
        particle.style.backgroundColor = color;
        particle.style.boxShadow = `0 0 10px ${color}, 0 0 20px ${color}`;
        particle.style.animationDuration = `${0.8 + Math.random() * 0.6}s`;
        container.appendChild(particle);
      }
      trackTimeout(() => container.remove(), 2000);
    };

    const executeDoubleJump = (col: number, row: number): void => {
      if (this.animationCancelled || !cubePositioner) return;
      const x1 = col * cellSize;
      const y1 = row * cellSize;
      const c2 = col;
      const r2 = row + 1;
      const x2 = c2 * cellSize;
      const y2 = r2 * cellSize;
      const r3 = row + 2;
      const x3 = c2 * cellSize;
      const y3 = r3 * cellSize;

      cubePositioner.style.transition = 'transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1)';
      cubePositioner.style.transform = `translate3d(${(x1 + x2) / 2}px, ${(y1 + y2) / 2}px, 150px)`;
      // First leap rises — wind-up whoosh. Muted: paso 6 of the table is
      // intentionally silent so only jump3 ramps into the smash (the
      // rising just before jump1 is left audio-less).
      if (!this.jump1WhooshMuted) this.playJumpWhoosh();

      trackTimeout(() => {
        if (this.animationCancelled || !cubePositioner) return;
        cubePositioner.style.transition = 'transform 0.2s ease-in';
        cubePositioner.style.transform = `translate3d(${x2}px, ${y2}px, 40px)`;

        trackTimeout(() => {
          // (jump2 fall ends here — see note above)
          if (this.animationCancelled) return;
          clearTile(r2, c2);
          // First leap lands — subtle inter-jump tick (= the missing
          // "caída del primer salto" cue). Quieter than a full footstep so it
          // doesn't compete with the smash audio 660ms later.
          //
          // 150ms (not 200) so the tick aligns with the visual landing
          // rather than firing after the cube has fully settled. The jump2
          // transition is `0.2s ease-in` (cubic-bezier(0.42,0,1,1)), which
          // is back-loaded — y reaches ~0.88 at 75% time, so the cube is
          // visually landing around 150ms and 200ms is post-settle silence.
          this.playInterStep();

          trackTimeout(() => {
            if (this.animationCancelled || !cubePositioner) return;
            cubePositioner.style.transition =
              'transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)';
            cubePositioner.style.transform = `translate3d(${x2}px, ${(y2 + y3) / 2}px, 350px)`;
            // Second leap rises — whoosh ramps the build-up to the smash.
            this.playJumpWhoosh();

            trackTimeout(() => {
              if (this.animationCancelled || !cubePositioner) return;
              cubePositioner.style.transition =
                'transform 0.15s ease-in, opacity 0.2s 0.1s';
              cubePositioner.style.transform = `translate3d(${x3}px, ${y3}px, -20px) scale(0)`;
              cubePositioner.style.opacity = '0';

              trackTimeout(() => {
                if (this.animationCancelled) return;
                flash.classList.add('active');
                trackTimeout(() => flash.classList.remove('active'), 80);
                tiles.forEach((t) => t.classList.add('cleared'));
                createExplosion(x3 + cellSize / 2, y3 + cellSize / 2);

                trackTimeout(() => {
                  if (this.animationCancelled) return;
                  this.advanceToLoadingApp();
                }, CUBE_POST_REVEAL_HOLD_MS);
              }, 140);
            }, 400);
          }, 100);
        }, 150);
      }, 250);
    };

    trackTimeout(moveCube, 800);
    this.scheduleSmashAudio();
  }

  /**
   * Plays a fresh Audio element at the footstep track. Each step creates its
   * own instance so overlapping footsteps play in parallel without restarting
   * a shared element. Honors `audioMuted` — muted intros skip footstep audio.
   */
  private playFootstep(): void {
    if (this.audioMuted) return;
    const audio = new Audio(this.footstepAudioSrc);
    audio.play().catch(() => undefined);
  }

  /**
   * Plays the swing/whoosh for the cube's leaping motion. Triggered in place
   * of the footstep on the last 2 path steps (right before the double jump),
   * matching the visual wind-up before the leap.
   */
  private playJumpWhoosh(): void {
    if (this.audioMuted) return;
    const audio = new Audio(this.whooshAudioSrc);
    audio.play().catch(() => undefined);
  }

  /**
   * Subtle tick for the inter-jump transitions of the double jump (between
   * jump1 and jump2, between jump2 and jump3). The source file is a very
   * quiet ~180ms click — meant as a soft cue, not a thud.
   */
  private playInterStep(): void {
    if (this.audioMuted) return;
    // The source peaks at ~0.024, much quieter than the footstep/whoosh
    // (0.94 / 0.17), so we boost the playback rate-of-fire by stacking a
    // second instance slightly offset in time — the two clicks reinforce
    // each other above the master volume ceiling of 1.0.
    const a = new Audio(this.interStepAudioSrc);
    const b = new Audio(this.interStepAudioSrc);
    a.play().catch(() => undefined);
    window.setTimeout(() => b.play().catch(() => undefined), 35);
  }

  private scheduleSmashAudio(): void {
    this.cancelScheduledSmashAudio();
    this.smashPlayScheduled = window.setTimeout(() => {
      this.smashPlayScheduled = null;
      this.smashPlayScheduledFired = true;
      void this.playSmashAudio();
    }, AUDIO_PLAY_DELAY_MS);
  }

  private cancelScheduledSmashAudio(): void {
    if (this.smashPlayScheduled !== null) {
      clearTimeout(this.smashPlayScheduled);
      this.smashPlayScheduled = null;
    }
  }

  private async playSmashAudio(): Promise<void> {
    if (!this.smashAudio) return;
    this.smashAudio.setMuted(this.audioMuted);
    try {
      await this.smashAudio.play();
      this.audioNeedsGesture = false;
    } catch {
      this.audioNeedsGesture = true;
    }
  }

  private renderLogo() {
    const tiles = [];
    for (let r = 0; r < 6; r++) {
      for (let c = 0; c < 4; c++) {
        tiles.push(html`<div class="intro-cube-tile" data-pos=${`${r}-${c}`}></div>`);
      }
    }
    return html`
      <div class="intro-cube-scene">
        <div class="intro-cube-board">
          <img class="intro-logo" src="/images/logo.png" alt="Juegazo" />
          <div class="intro-cube-grid">${tiles}</div>
          <div class="intro-cube-positioner">
            <div class="intro-cube-rotator">
              <div class="intro-cube">
                <div class="intro-cube-face intro-cube-face--front"></div>
                <div class="intro-cube-face intro-cube-face--back"></div>
                <div class="intro-cube-face intro-cube-face--right"></div>
                <div class="intro-cube-face intro-cube-face--left"></div>
                <div class="intro-cube-face intro-cube-face--top"></div>
                <div class="intro-cube-face intro-cube-face--bottom"></div>
              </div>
            </div>
          </div>
          <div class="intro-cube-flash"></div>
        </div>
      </div>
    `;
  }

  private renderLoader() {
    const word1 = 'CARGANDO';
    const word2 = 'ESPERE';
    const cubeCount = Math.max(word1.length, word2.length);
    const letters = Array.from({ length: cubeCount }, (_, i) => {
      // Cada cubo tiene 5 caras (front/top/back/bottom/left).
      // Ponemos la letra de word1 en la cara frontal y la de word2 en la
      // cara opuesta (back). El resto vacias. La animacion 'rotate' del
      // CodePen rota el cubo de 0deg -> 90deg -> 180deg, asi el usuario ve
      // alternadamente word1 -> word2 -> word1 en cada cubo.
      const c1 = word1[i] ?? '';
      const c2 = word2[i] ?? '';
      const panels = [c1, c2, '', '', ''];
      const isOrange = i % 2 === 0;
      const hue = isOrange ? 35 : 190;
      const sat = isOrange ? 100 : 70;
      const light = isOrange ? 55 : 50;
      return html`
        <div
          class="jgz-loader__letter-wrap"
          style="--index: ${i}; --hue: ${hue}; --saturation: ${sat}; --lightness: ${light};"
        >
          <div class="jgz-loader__letter" data-letter-front=${c1} data-letter-top=${c2}>
            ${panels.map(
              (p) => html`<span class="jgz-loader__panel" aria-hidden="true">${p}</span>`,
            )}
          </div>
        </div>
      `;
    });
    return html`
      <div class="jgz-loader">
        <div class="jgz-loader__scene">
          <div class="jgz-loader__word">${letters}</div>
        </div>
      </div>
    `;
  }

  private renderAudioToggle() {
    const { icon, label, modifier } = this.audioToggleProps();
    return html`
      <button
        class="intro-audio-toggle ${modifier}"
        type="button"
        aria-label=${label}
        aria-pressed=${this.audioMuted ? 'true' : 'false'}
        @click=${this.handleAudioToggleClick}
      >
        <span class="intro-audio-toggle__icon" aria-hidden="true">${icon}</span>
        <span class="intro-audio-toggle__label">${label}</span>
      </button>
    `;
  }

  private audioToggleProps(): { icon: string; label: string; modifier: string } {
    if (this.audioNeedsGesture) {
      return { icon: '🔈', label: 'Activar audio', modifier: 'is-blocked' };
    }
    return this.audioMuted
      ? { icon: '🔈', label: 'Activar sonido', modifier: 'is-muted' }
      : { icon: '🔊', label: 'Silenciar', modifier: 'is-playing' };
  }

  private handleAudioToggleClick = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();

    if (this.audioNeedsGesture) {
      // Click while playback is blocked. Either:
      //   (a) the scheduled smash hasn't fired yet — let it fire at
      //       AUDIO_PLAY_DELAY_MS so the hit lands on the cube's smash.
      //   (b) the scheduled smash already fired and failed — play now so
      //       the user still hears *something* (misaligned, but audible).
      this.audioNeedsGesture = false;
      if (this.smashPlayScheduledFired) {
        void this.playSmashAudio();
      }
      return;
    }

    this.audioMuted = !this.audioMuted;
    // Persist the new state so the game view's sound toggle reflects whatever
    // the user chose here. Without this, the intro decision is lost when the
    // user moves on to the play view.
    audioManager.setEnabled(!this.audioMuted);
  };

  render() {
    if (this.phase === 'done') return nothing;
    const showLoader = this.phase === 'loading-app';
    return html`
      <div class="intro-shell" role="presentation">
        ${showLoader ? this.renderLoader() : this.renderLogo()}
        ${!showLoader ? this.renderAudioToggle() : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'juegazo-intro': JuegazoIntro;
  }
}